import React, { Fragment, useEffect, useState } from "react";
import api from "../../api";
import { formatDateDDMMYYYY } from "../../util/formatDate";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import Badge from "../../components/Badge";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
    ReportShell,
    ReportStatCard,
    ReportPanel,
} from "../../components/reports/ReportShell";
import {
    ReportDateFilterMenu,
    ReportComboboxFilterMenu,
} from "../../components/reports/ReportFilters";

ChartJS.register(
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

const colors = [
    "#0ea5e9",
    "#14b8a6",
    "#f97316",
    "#fb7185",
    "#8b5cf6",
    "#facc15",
    "#38bdf8",
    "#34d399",
];

const options = [
    { name: "Datos generales", href: "/reports/general-data-report" },
    { name: "Cirugias", href: "#" },
    { name: "Estudios", href: "/reports/study-report" },
    { name: "Medicamentos", href: "/reports/medication-report" },
];

export default function SurgeriesPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [loading, setLoading] = useState(true);
    const [surgeries, setSurgeries] = useState();
    const [data, setData] = useState();
    const [filters, setFilters] = useState({});
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [surgeryFilter, setSurgeryFilter] = useState();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const handleFromDate = (field, value) => setFromDate(value);
    const handleToDate = (field, value) => setToDate(value);
    const handleSurgeryFilter = (field, value) => setSurgeryFilter(value);

    useEffect(() => {
        if (isDesktop) setSidebarOpen(false);
    }, [isDesktop]);

    useEffect(() => {
        api.surgeries.getMulti().then((response) => setSurgeries(response));
    }, []);

    useEffect(() => {
        setLoading(true);
        const aux = new URLSearchParams();

        Object.keys(filters || {}).forEach((prop) => {
            const value = filters[prop];
            if (Array.isArray(value)) {
                value.forEach((element) => aux.append(prop, element.id));
            } else if (value !== undefined && value !== null) {
                aux.append(prop, value);
            }
        });

        const request =
            aux.toString().length > 0
                ? api.surgeryReport.getReports(aux)
                : api.surgeryReport.getReports();

        request
            .then((response) => setData(response))
            .finally(() => setLoading(false));
    }, [filters]);

    const saveFromDate = () => {
        if (fromDate) setFilters((prev) => ({ ...prev, date_after: fromDate }));
    };

    const saveToDate = () => {
        if (toDate) setFilters((prev) => ({ ...prev, date_before: toDate }));
    };

    const saveSurgeryFilter = () => {
        if (surgeryFilter?.length) {
            setFilters((prev) => ({ ...prev, surgery: surgeryFilter }));
        }
    };
    const applyDateFilters = () => {
        saveToDate();
        saveFromDate();
    };

    const deleteFromDate = () => {
        const state = { ...filters };
        delete state.date_after;
        setFilters(state);
        setFromDate(undefined);
    };

    const deleteToDate = () => {
        const state = { ...filters };
        delete state.date_before;
        setFilters(state);
        setToDate(undefined);
    };

    const deleteDates = () => {
        const state = { ...filters };
        delete state.date_after;
        delete state.date_before;
        setFilters(state);
        setFromDate(undefined);
        setToDate(undefined);
    };

    const deleteAllSurgeryFilter = () => {
        const state = { ...filters };
        delete state.surgery;
        setFilters(state);
        setSurgeryFilter(undefined);
    };

    const deleteSurgeryFilter = (value) => {
        setFilters((prev) => ({
            ...prev,
            surgery: (prev.surgery || []).filter((element) => element.id !== value),
        }));
        setSurgeryFilter((prev) =>
            (prev || []).filter((element) => element.id !== value),
        );
    };

    const pieData = data
        ? {
              labels: data.charts.surgeryOrderPatientsByTypes
                  .filter((element) => element.quantity > 0)
                  .map((element) => element.name),
              datasets: [
                  {
                      data: data.charts.surgeryOrderPatientsByTypes
                          .filter((element) => element.quantity > 0)
                          .map((element) => element.quantity),
                      backgroundColor: colors,
                  },
              ],
          }
        : undefined;

    const totalWithOrder = data?.surgeryOrderPatients || 0;
    const totalPerformed = data?.surgeriesPerformedPatients || 0;
    const pending = Math.max(0, totalWithOrder - totalPerformed);

    const optionsPie = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: isDesktop ? "left" : "bottom",
            },
        },
    };

    return (
        <ReportShell
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            setSidebarOpenDesktop={setSidebarOpenDesktop}
            isDesktop={isDesktop}
            loading={loading}
            currentLabel="Cirugias"
            options={options}
            title="Seguimiento de demanda quirurgica"
            actions={
                <>
                    <ReportDateFilterMenu
                        handleFromDate={handleFromDate}
                        handleToDate={handleToDate}
                        onClear={deleteDates}
                        onApply={applyDateFilters}
                    />
                    <ReportComboboxFilterMenu
                        label="Cirugias"
                        comboboxLabel="Cirugias"
                        options={surgeries}
                        handleChange={handleSurgeryFilter}
                        name="surgeries"
                        onClear={deleteAllSurgeryFilter}
                        onApply={saveSurgeryFilter}
                    />
                </>
            }
            badges={
                <>
                    {filters.date_after && <Badge text={`Desde: ${formatDateDDMMYYYY(filters.date_after)}`} handleDelete={deleteFromDate} />}
                    {filters.date_before && <Badge text={`Hasta: ${formatDateDDMMYYYY(filters.date_before)}`} handleDelete={deleteToDate} />}
                    {(filters.surgery || []).map((element) => (
                        <Badge key={element.id} text={element.name} handleDelete={() => deleteSurgeryFilter(element.id)} />
                    ))}
                </>
            }
            summary={[
                <ReportStatCard key="orders" label="Pacientes con orden de cirugia" value={totalWithOrder} tone="amber" />,
                <ReportStatCard key="performed" label="Cirugias realizadas" value={totalPerformed} tone="emerald" />,
                <ReportStatCard key="pending" label="Pacientes pendientes" value={pending} tone="rose" />,
                <ReportStatCard
                    key="resolution"
                    label="Nivel de resolucion"
                    value={totalWithOrder ? `${Math.round((totalPerformed / totalWithOrder) * 100)}%` : "0%"}
                    tone="blue"
                />,
            ]}
        >
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
                <ReportPanel title="Tipos de cirugia">
                    {pieData ? (
                        <div className="h-[340px] sm:h-[420px]">
                            <Pie options={optionsPie} data={pieData} />
                        </div>
                    ) : null}
                </ReportPanel>

                <ReportPanel title="Resumen">
                    <div className="space-y-4">
                        <ReportStatCard label="Pacientes con orden" value={totalWithOrder} tone="amber" />
                        <ReportStatCard label="Ya operados" value={totalPerformed} tone="emerald" />
                        <ReportStatCard label="Aun sin resolver" value={pending} tone="rose" />
                    </div>
                </ReportPanel>
            </div>
        </ReportShell>
    );
}